import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

interface DocumentSearchBarProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onSearch: () => void;
  onAddDocument: () => void;
  searching: boolean;
  canAddCustomDocs: boolean;
}

export const DocumentSearchBar: React.FC<DocumentSearchBarProps> = ({
  searchQuery,
  onSearchQueryChange,
  onSearch,
  onAddDocument,
  searching,
  canAddCustomDocs
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const handleClear = () => {
    onSearchQueryChange('');
    // Parent component's useEffect will automatically clear search results when query is empty
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid size={{ xs: 'grow' }}>
          <TextField
            fullWidth
            placeholder={t('documents.search.placeholder')}
            variant="outlined"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ mr: 1 }}
            slotProps={{
              input: {
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('documents.search.clear')}
                      onClick={handleClear}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size="auto">
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={onSearch}
            disabled={searching || !searchQuery.trim()}
          >
            {searching ? <CircularProgress size={24} /> : t('documents.search.button')}
          </Button>
        </Grid>
        {canAddCustomDocs && (
          <Grid size="auto">
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddDocument}
            >
              {t('documents.button.addDocument')}
            </Button>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};